/**
 * Simplified Feature Flags & Environment Detection
 * Direct .env variable reading with descriptive names and safe fallbacks
 */

// ============================================================================
// ENVIRONMENT DETECTION
// ============================================================================

export const isDevelopment = (): boolean => import.meta.env.DEV;

export const isStaging = (): boolean =>
  import.meta.env.PROD && import.meta.env.VITE_ENVIRONMENT === 'staging';

export const isProduction = (): boolean =>
  import.meta.env.PROD && import.meta.env.VITE_ENVIRONMENT === 'production';

export const getEnvironment = (): 'development' | 'staging' | 'production' => {
  if (isDevelopment()) {
    return 'development';
  }
  if (isStaging()) {
    return 'staging';
  }
  if (isProduction()) {
    return 'production';
  }

  // Safe fallback
  console.warn('Unknown environment detected, defaulting to development');
  return 'development';
};

/**
 * Sentry reporting is enabled only in staging and production.
 * Dev builds never ship events.
 */
export const isSentryEnabled = (): boolean => isStaging() || isProduction();

// ============================================================================
// FEATURE FLAGS - FEATURES
// ============================================================================

export const shouldEnableInvitationWidget = (): boolean => {
  return import.meta.env.VITE_DEV_INVITATION_WIDGET_ENABLED === 'true';
};

export const shouldShowDesignSystemPage = (): boolean => {
  return isDevelopment();
};

/**
 * Sentry wiring-verification page — mirrors the backend's /debug/sentry
 * route. Available everywhere Sentry can plausibly be checked (dev +
 * staging), excluded from production so it's never a real-user-facing
 * footgun.
 */
export const shouldShowSentryDebugPage = (): boolean => {
  return !isProduction();
};

// ============================================================================
// FEATURE FLAGS - DEVELOPMENT FEATURES
// ============================================================================

export const isAuthRequired = (): boolean => {
  return import.meta.env.VITE_AUTH_REQUIRE_LOGIN !== 'false';
};

export const shouldUseMockData = (): boolean => {
  // CRITICAL: Never use mock data in staging or production
  if (isStaging() || isProduction()) {
    return false;
  }

  // Only allow mock data in development when explicitly enabled
  return isDevelopment() && import.meta.env.VITE_DEV_MOCK_DATA === 'true';
};

export const shouldEnableDebugLogging = (): boolean => {
  return import.meta.env.VITE_DEV_DEBUG_LOGGING === 'true';
};

export const shouldEnablePerformanceMonitoring = (): boolean => {
  return import.meta.env.VITE_DEV_PERFORMANCE_MONITORING === 'true';
};

/**
 * Get development admin token for API authentication bypass
 * Only available in development environment
 */
export const getDevAdminToken = (): string | null => {
  return isDevelopment() ? (import.meta.env.VITE_DEV_ADMIN_TOKEN as string) || null : null;
};

// ============================================================================
// FEATURE FLAGS - FUTURE FEATURES (Templates for when needed)
// ============================================================================

// Real-time Features
// export const isRealtimeEnabled = (): boolean => {
//   return import.meta.env.VITE_REALTIME_ENABLED === 'true';
// };

// export const isWebSocketEnabled = (): boolean => {
//   return import.meta.env.VITE_WEBSOCKET_ENABLED === 'true';
// };

// UI Features
// export const isDarkModeEnabled = (): boolean => {
//   return import.meta.env.VITE_DARK_MODE_ENABLED === 'true';
// };

// export const isAdvancedChartsEnabled = (): boolean => {
//   return import.meta.env.VITE_ADVANCED_CHARTS_ENABLED === 'true';
// };

// export const isFloorplanEnabled = (): boolean => {
//   return import.meta.env.VITE_FLOORPLAN_ENABLED === 'true';
// };

// Asset Management Features
// export const isAssetAutoRefreshEnabled = (): boolean => {
//   return import.meta.env.VITE_ASSET_AUTO_REFRESH_ENABLED === 'true';
// };

// export const isGeofencingEnabled = (): boolean => {
//   return import.meta.env.VITE_GEOFENCING_ENABLED === 'true';
// };

// export const isAssetAlertsEnabled = (): boolean => {
//   return import.meta.env.VITE_ASSET_ALERTS_ENABLED === 'true';
// };

// ============================================================================
// BUILD-TIME VALIDATION
// ============================================================================

/**
 * Validate production configuration at build time.
 * Throws an error if invalid configuration is detected.
 * Call this in main.tsx before app initialization.
 */
export const validateProductionConfig = (): void => {
  // Only validate in staging/production builds
  if (!isStaging() && !isProduction()) {
    return;
  }

  const errors: string[] = [];

  // CRITICAL: Ensure mock data is disabled in production
  if (import.meta.env.VITE_DEV_MOCK_DATA === 'true') {
    errors.push('Mock data is enabled in production environment (VITE_DEV_MOCK_DATA=true)');
  }

  // Warn about debug logging in production (not fatal)
  if (import.meta.env.VITE_DEV_DEBUG_LOGGING === 'true') {
    console.warn('⚠️ Debug logging is enabled in production environment');
  }

  // Ensure authentication is required
  if (import.meta.env.VITE_AUTH_REQUIRE_LOGIN === 'false') {
    errors.push(
      'Authentication is disabled in production environment (VITE_AUTH_REQUIRE_LOGIN=false)'
    );
  }

  // Ensure backend target is not 'local'
  if (import.meta.env.VITE_BACKEND_TARGET === 'local') {
    errors.push('Backend target is set to "local" in production environment');
  }

  // Ensure Firebase emulator is not enabled
  if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
    errors.push(
      'Firebase emulator is enabled in production environment (VITE_USE_FIREBASE_EMULATOR=true)'
    );
  }

  // If any errors, throw
  if (errors.length > 0) {
    const message = [
      '❌ PRODUCTION CONFIGURATION ERROR ❌',
      '',
      'The following configuration errors will prevent deployment:',
      ...errors.map(e => `  • ${e}`),
      '',
      'Fix these issues in your environment configuration before deploying.',
    ].join('\n');

    throw new Error(message);
  }
};

// ============================================================================
// DEV BOOTSTRAP
// ============================================================================

/**
 * Seed localStorage with default factory/tenant IDs when auth is bypassed.
 * Reads from VITE_DEV_DEFAULT_FACTORY_ID and VITE_DEV_DEFAULT_TENANT_ID.
 * Only runs in development when auth is not required.
 */
export const bootstrapDevDefaults = (): void => {
  if (!isDevelopment() || isAuthRequired()) {
    return;
  }

  const defaultFactoryId = import.meta.env.VITE_DEV_DEFAULT_FACTORY_ID;
  const defaultTenantId = import.meta.env.VITE_DEV_DEFAULT_TENANT_ID;

  if (defaultFactoryId && !localStorage.getItem('current-factory-id')) {
    // atomWithStorage wraps values in JSON
    localStorage.setItem('current-factory-id', JSON.stringify(defaultFactoryId));
  }

  if (defaultTenantId && !localStorage.getItem('selected_tenant_id')) {
    localStorage.setItem('selected_tenant_id', defaultTenantId);
  }
};

// ============================================================================
// DEBUGGING & LOGGING
// ============================================================================

export const logFeatureFlagsAndEnvironment = (): void => {
  if (shouldEnableDebugLogging()) {
    console.group('🔧 Feature Flags & Environment Configuration');
    console.log('Environment:', getEnvironment());
    console.log('Is Development:', isDevelopment());
    console.log('Is Staging:', isStaging());
    console.log('Is Production:', isProduction());
    console.log('---');
    console.log('Feature Flags:');
    console.log('  Auth Required:', isAuthRequired());
    console.log('  Use Mock Data:', shouldUseMockData());
    console.log('  Debug Logging:', shouldEnableDebugLogging());
    console.log('  Performance Monitoring:', shouldEnablePerformanceMonitoring());
    console.log('---');
    console.log('Environment Variables:');
    console.log('  VITE_ENVIRONMENT:', import.meta.env.VITE_ENVIRONMENT);
    console.log('  VITE_AUTH_REQUIRE_LOGIN:', import.meta.env.VITE_AUTH_REQUIRE_LOGIN);
    console.log('  VITE_DEV_MOCK_DATA:', import.meta.env.VITE_DEV_MOCK_DATA);
    console.log('  VITE_DEV_DEBUG_LOGGING:', import.meta.env.VITE_DEV_DEBUG_LOGGING);
    console.log(
      '  VITE_DEV_PERFORMANCE_MONITORING:',
      import.meta.env.VITE_DEV_PERFORMANCE_MONITORING
    );
    console.groupEnd();
  }
};
